import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import styles from './styles.module.scss';
import { displayErrorMessage } from '@utils/displayErrorMessage';
import { getAddressSuggestionsWithDebounce } from '@registration/sagas';
import { getPropertySuggestionsOpenWithDebounce, getPropertySuggestionsWithDebounce } from '@propertyReports/sagas';


const SUGGESTIONS_ACTIONS = {
  harmony: getAddressSuggestionsWithDebounce,
  domain: getPropertySuggestionsWithDebounce,
  'domain-open': getPropertySuggestionsOpenWithDebounce,
};

const getAddressLabel = (address) => (typeof address === 'string' ? address : (address?.address ?? address?.fullAddress ?? ''));

const getHarmonyAddressLabel = (address) => (typeof address === 'string' ? address : (address?.fullAddress || address?.address || ''));

const AddressAutocomplete = ({
  autoComplete,
  autocompleteTestId,
  className,
  disableClearable,
  disabled,
  fullWidth,
  initialOptions,
  inputTestId,
  isOptionEqualToValue,
  onChange,
  onInputChange,
  onLoadingChange,
  openOnFocus,
  placeholder,
  provider,
  searchErrorMessage,
  style,
  textFieldClassName,
  textFieldProps,
  value,
}) => {
  const dispatch = useDispatch();
  const latestSearch = useRef('');
  const previousValue = useRef(value);
  const [inputValue, setInputValue] = useState(getAddressLabel(value));
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);

  const updateLoading = (nextValue) => {
    setIsLoading(nextValue);
    if (onLoadingChange) onLoadingChange(nextValue);
  };

  useEffect(() => {
    if (initialOptions) setOptions(initialOptions);
  }, [initialOptions]);

  useEffect(() => {
    if (previousValue.current !== value) {
      const nextInputValue = getAddressLabel(value);
      latestSearch.current = nextInputValue;
      setInputValue(nextInputValue);
      setOptions([]);
      setIsOpen(false);
      updateLoading(false);
    }
    previousValue.current = value;
  }, [value]); //eslint-disable-line

  const search = (searchString) => {
    latestSearch.current = searchString;
    setInputValue(searchString);
    if (onInputChange) onInputChange(searchString);

    if (!searchString.length) {
      setOptions([]);
      setIsOpen(false);
      updateLoading(false);
      return;
    }

    setIsOpen(true);

    if (searchString.length < 3) {
      setOptions([]);
      updateLoading(false);
      return;
    }

    setOptions([]);
    updateLoading(true);
    dispatch(SUGGESTIONS_ACTIONS[provider]({
      address: searchString,
      onSuccess: (data) => {
        if (latestSearch.current !== searchString) return;
        setOptions(data?.slice?.(0, 20) ?? []);
        updateLoading(false);
        setIsOpen(true);
      },
      onError: (data) => {
        if (latestSearch.current !== searchString) return;
        displayErrorMessage(data, searchErrorMessage);
        updateLoading(false);
        setIsOpen(false);
      }
    }));
  };

  const searchHarmony = (searchString) => {
    latestSearch.current = searchString;
    if (onInputChange) onInputChange(searchString);

    if (searchString?.length >= 3) {
      dispatch(SUGGESTIONS_ACTIONS.harmony({
        address: searchString,
        onSuccess: (data) => {
          if (latestSearch.current !== searchString) return;
          setOptions(data?.slice?.(0, 20) ?? []);
        },
        onError: (data) => {
          if (latestSearch.current !== searchString) return;
          displayErrorMessage(data, searchErrorMessage);
        }
      }));
    } else {
      setOptions([]);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      search(newInputValue);
      return;
    }

    latestSearch.current = newInputValue;
    setInputValue(newInputValue);
    setIsOpen(false);
    updateLoading(false);
    if (onInputChange) onInputChange(newInputValue);
  };

  const {
    InputProps: suppliedInputProps,
    inputProps: suppliedHtmlInputProps,
    ...remainingTextFieldProps
  } = textFieldProps;

  if (provider === 'harmony') {
    return (
      <Autocomplete
        data-testid={autocompleteTestId}
        autoComplete={autoComplete}
        openOnFocus={openOnFocus}
        disableClearable={disableClearable}
        className={className}
        disabled={disabled}
        options={options}
        getOptionLabel={getHarmonyAddressLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={(availableOptions) => availableOptions}
        style={fullWidth ? { width: '100%', ...style } : style}
        value={value ?? null}
        onChange={(event, newValue) => onChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            {...remainingTextFieldProps}
            className={textFieldClassName}
            InputProps={suppliedInputProps ? { ...params.InputProps, ...suppliedInputProps } : params.InputProps}
            inputProps={{
              ...(params.inputProps ?? {}),
              ...suppliedHtmlInputProps,
              'data-testid': inputTestId,
              maxLength: 500,
            }}
            onChange={(event) => searchHarmony(event.target.value)}
            onFocus={(event) => {
              if (!event.target.value) searchHarmony(event.target.value);
            }}
            placeholder={placeholder}
            variant="outlined"
          />
        )}
      />
    );
  }

  return (
    <Autocomplete
      autoComplete
      data-testid={autocompleteTestId}
      className={className}
      disabled={disabled}
      filterOptions={(availableOptions) => availableOptions}
      getOptionLabel={getAddressLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selectedValue) => (
        option?.id ?? option?.fullAddress ?? option?.address
      ) === (
        selectedValue?.id ?? selectedValue?.fullAddress ?? selectedValue?.address
      )}
      loading={isLoading}
      noOptionsText={inputValue.length < 3 ? 'Type at least 3 characters' : 'No options'}
      onChange={(event, newValue) => onChange(newValue)}
      onClose={() => setIsOpen(false)}
      onInputChange={handleInputChange}
      onOpen={() => {
        if (inputValue.length) setIsOpen(true);
      }}
      open={isOpen}
      options={options}
      value={value ?? null}
      renderInput={(params) => (
        <TextField
          {...params}
          {...remainingTextFieldProps}
          className={textFieldClassName}
          InputProps={{
            ...params.InputProps,
            ...suppliedInputProps,
            endAdornment: isLoading
              ? <CircularProgress size={18} className={styles.progressIcon} />
              : (suppliedInputProps?.endAdornment ?? params.InputProps.endAdornment),
          }}
          inputProps={{
            ...params.inputProps,
            ...suppliedHtmlInputProps,
            'data-testid': inputTestId,
            maxLength: 500,
          }}
          placeholder={placeholder ?? ''}
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
};

AddressAutocomplete.defaultProps = {
  autoComplete: false,
  autocompleteTestId: undefined,
  className: undefined,
  disableClearable: false,
  disabled: false,
  fullWidth: true,
  initialOptions: undefined,
  inputTestId: undefined,
  isOptionEqualToValue: undefined,
  onInputChange: undefined,
  onLoadingChange: undefined,
  openOnFocus: false,
  placeholder: undefined,
  provider: 'harmony',
  searchErrorMessage: 'Unable to retrieve address suggestions',
  style: undefined,
  textFieldClassName: undefined,
  textFieldProps: {},
  value: null,
};

AddressAutocomplete.propTypes = {
  autoComplete: PropTypes.bool,
  autocompleteTestId: PropTypes.string,
  className: PropTypes.string,
  disableClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  initialOptions: PropTypes.array,
  inputTestId: PropTypes.string,
  isOptionEqualToValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  openOnFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  provider: PropTypes.oneOf(['harmony', 'domain', 'domain-open']),
  searchErrorMessage: PropTypes.string,
  style: PropTypes.object,
  textFieldClassName: PropTypes.string,
  textFieldProps: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default AddressAutocomplete;
